"use client";

import React, { useState } from "react";
import { Button } from "@/app/components/ui/button";
import { 
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/app/components/ui/drawer";
import { 
  Download as DownloadIcon,
  Image as ImageIcon,
  Video as VideoIcon,
  Music as AudioIcon,
  FileText as DocumentIcon,
  Archive as ArchiveIcon,
  Code as CodeIcon,
  Palette as DesignIcon,
} from "lucide-react";
import { cn } from "@/app/lib/utils";

export interface DownloadOption {
  id: string;
  label: string;
  extension: string;
  mimeType: string;
  icon: React.ReactNode;
  category: 'image' | 'video' | 'audio' | 'document' | 'archive' | 'code' | 'design';
}

export interface DownloadComponentProps {
  data: string | Blob | File;
  filename?: string;
  className?: string;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  children?: React.ReactNode;
  disabled?: boolean;
  onDownload?: (format: string, filename: string) => void;
  allowedCategories?: ('image' | 'video' | 'audio' | 'document' | 'archive' | 'code' | 'design')[];
}

const DOWNLOAD_OPTIONS: DownloadOption[] = [
  // Image formats
  { id: 'png', label: 'PNG', extension: 'png', mimeType: 'image/png', icon: <ImageIcon className="h-4 w-4" />, category: 'image' },
  { id: 'jpg', label: 'JPG', extension: 'jpg', mimeType: 'image/jpeg', icon: <ImageIcon className="h-4 w-4" />, category: 'image' },
  { id: 'jpeg', label: 'JPEG', extension: 'jpeg', mimeType: 'image/jpeg', icon: <ImageIcon className="h-4 w-4" />, category: 'image' },
  { id: 'gif', label: 'GIF', extension: 'gif', mimeType: 'image/gif', icon: <ImageIcon className="h-4 w-4" />, category: 'image' },
  { id: 'webp', label: 'WebP', extension: 'webp', mimeType: 'image/webp', icon: <ImageIcon className="h-4 w-4" />, category: 'image' },
  { id: 'svg', label: 'SVG', extension: 'svg', mimeType: 'image/svg+xml', icon: <ImageIcon className="h-4 w-4" />, category: 'image' },
  { id: 'bmp', label: 'BMP', extension: 'bmp', mimeType: 'image/bmp', icon: <ImageIcon className="h-4 w-4" />, category: 'image' },
  { id: 'ico', label: 'ICO', extension: 'ico', mimeType: 'image/x-icon', icon: <ImageIcon className="h-4 w-4" />, category: 'image' },
  { id: 'tiff', label: 'TIFF', extension: 'tiff', mimeType: 'image/tiff', icon: <ImageIcon className="h-4 w-4" />, category: 'image' },
  
  // Video formats
  { id: 'mp4', label: 'MP4', extension: 'mp4', mimeType: 'video/mp4', icon: <VideoIcon className="h-4 w-4" />, category: 'video' },
  { id: 'avi', label: 'AVI', extension: 'avi', mimeType: 'video/x-msvideo', icon: <VideoIcon className="h-4 w-4" />, category: 'video' },
  { id: 'mov', label: 'MOV', extension: 'mov', mimeType: 'video/quicktime', icon: <VideoIcon className="h-4 w-4" />, category: 'video' },
  { id: 'wmv', label: 'WMV', extension: 'wmv', mimeType: 'video/x-ms-wmv', icon: <VideoIcon className="h-4 w-4" />, category: 'video' },
  { id: 'flv', label: 'FLV', extension: 'flv', mimeType: 'video/x-flv', icon: <VideoIcon className="h-4 w-4" />, category: 'video' },
  { id: 'webm', label: 'WebM', extension: 'webm', mimeType: 'video/webm', icon: <VideoIcon className="h-4 w-4" />, category: 'video' },
  { id: 'mkv', label: 'MKV', extension: 'mkv', mimeType: 'video/x-matroska', icon: <VideoIcon className="h-4 w-4" />, category: 'video' },
  { id: '3gp', label: '3GP', extension: '3gp', mimeType: 'video/3gpp', icon: <VideoIcon className="h-4 w-4" />, category: 'video' },
  
  // Audio formats
  { id: 'mp3', label: 'MP3', extension: 'mp3', mimeType: 'audio/mpeg', icon: <AudioIcon className="h-4 w-4" />, category: 'audio' },
  { id: 'wav', label: 'WAV', extension: 'wav', mimeType: 'audio/wav', icon: <AudioIcon className="h-4 w-4" />, category: 'audio' },
  { id: 'flac', label: 'FLAC', extension: 'flac', mimeType: 'audio/flac', icon: <AudioIcon className="h-4 w-4" />, category: 'audio' },
  { id: 'aac', label: 'AAC', extension: 'aac', mimeType: 'audio/aac', icon: <AudioIcon className="h-4 w-4" />, category: 'audio' },
  { id: 'ogg', label: 'OGG', extension: 'ogg', mimeType: 'audio/ogg', icon: <AudioIcon className="h-4 w-4" />, category: 'audio' },
  { id: 'wma', label: 'WMA', extension: 'wma', mimeType: 'audio/x-ms-wma', icon: <AudioIcon className="h-4 w-4" />, category: 'audio' },
  { id: 'm4a', label: 'M4A', extension: 'm4a', mimeType: 'audio/mp4', icon: <AudioIcon className="h-4 w-4" />, category: 'audio' },
  
  // Document formats
  { id: 'pdf', label: 'PDF', extension: 'pdf', mimeType: 'application/pdf', icon: <DocumentIcon className="h-4 w-4" />, category: 'document' },
  { id: 'doc', label: 'DOC', extension: 'doc', mimeType: 'application/msword', icon: <DocumentIcon className="h-4 w-4" />, category: 'document' },
  { id: 'docx', label: 'DOCX', extension: 'docx', mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', icon: <DocumentIcon className="h-4 w-4" />, category: 'document' },
  { id: 'txt', label: 'TXT', extension: 'txt', mimeType: 'text/plain', icon: <DocumentIcon className="h-4 w-4" />, category: 'document' },
  { id: 'rtf', label: 'RTF', extension: 'rtf', mimeType: 'application/rtf', icon: <DocumentIcon className="h-4 w-4" />, category: 'document' },
  { id: 'odt', label: 'ODT', extension: 'odt', mimeType: 'application/vnd.oasis.opendocument.text', icon: <DocumentIcon className="h-4 w-4" />, category: 'document' },
  
  // Archive formats
  { id: 'zip', label: 'ZIP', extension: 'zip', mimeType: 'application/zip', icon: <ArchiveIcon className="h-4 w-4" />, category: 'archive' },
  { id: 'rar', label: 'RAR', extension: 'rar', mimeType: 'application/vnd.rar', icon: <ArchiveIcon className="h-4 w-4" />, category: 'archive' },
  { id: '7z', label: '7Z', extension: '7z', mimeType: 'application/x-7z-compressed', icon: <ArchiveIcon className="h-4 w-4" />, category: 'archive' },
  { id: 'tar', label: 'TAR', extension: 'tar', mimeType: 'application/x-tar', icon: <ArchiveIcon className="h-4 w-4" />, category: 'archive' },
  { id: 'gz', label: 'GZ', extension: 'gz', mimeType: 'application/gzip', icon: <ArchiveIcon className="h-4 w-4" />, category: 'archive' },
  
  // Code formats
  { id: 'js', label: 'JS', extension: 'js', mimeType: 'text/javascript', icon: <CodeIcon className="h-4 w-4" />, category: 'code' },
  { id: 'ts', label: 'TS', extension: 'ts', mimeType: 'text/typescript', icon: <CodeIcon className="h-4 w-4" />, category: 'code' },
  { id: 'html', label: 'HTML', extension: 'html', mimeType: 'text/html', icon: <CodeIcon className="h-4 w-4" />, category: 'code' },
  { id: 'css', label: 'CSS', extension: 'css', mimeType: 'text/css', icon: <CodeIcon className="h-4 w-4" />, category: 'code' },
  { id: 'json', label: 'JSON', extension: 'json', mimeType: 'application/json', icon: <CodeIcon className="h-4 w-4" />, category: 'code' },
  { id: 'xml', label: 'XML', extension: 'xml', mimeType: 'application/xml', icon: <CodeIcon className="h-4 w-4" />, category: 'code' },
  { id: 'py', label: 'PY', extension: 'py', mimeType: 'text/x-python', icon: <CodeIcon className="h-4 w-4" />, category: 'code' },
  { id: 'java', label: 'JAVA', extension: 'java', mimeType: 'text/x-java-source', icon: <CodeIcon className="h-4 w-4" />, category: 'code' },
  
  // Design formats
  { id: 'psd', label: 'PSD', extension: 'psd', mimeType: 'image/vnd.adobe.photoshop', icon: <DesignIcon className="h-4 w-4" />, category: 'design' },
  { id: 'ai', label: 'AI', extension: 'ai', mimeType: 'application/postscript', icon: <DesignIcon className="h-4 w-4" />, category: 'design' },
  { id: 'eps', label: 'EPS', extension: 'eps', mimeType: 'application/postscript', icon: <DesignIcon className="h-4 w-4" />, category: 'design' },
  { id: 'sketch', label: 'SKETCH', extension: 'sketch', mimeType: 'application/vnd.sketch', icon: <DesignIcon className="h-4 w-4" />, category: 'design' },
  { id: 'figma', label: 'FIGMA', extension: 'fig', mimeType: 'application/figma', icon: <DesignIcon className="h-4 w-4" />, category: 'design' },
];

const CATEGORY_LABELS = {
  image: 'Images',
  video: 'Videos', 
  audio: 'Audio',
  document: 'Documents',
  archive: 'Archives',
  code: 'Code Files',
  design: 'Design Files',
};

// Function to detect file type from data
const detectFileType = (data: string | Blob | File): string | null => {
  if (typeof data === 'string') {
    if (data.startsWith('data:')) {
      // Extract MIME type from data URL
      const mimeMatch = data.match(/data:([^;]+)/);
      return mimeMatch ? mimeMatch[1] : null;
    }
    return null; // Plain text, no specific type
  } else if (data instanceof Blob) {
    return data.type || null;
  } else if (data && typeof data === 'object' && 'type' in data) {
    return (data as any).type || null;
  }
  return null;
};

// Function to get allowed categories based on file type
const getAllowedCategories = (mimeType: string | null): ('image' | 'video' | 'audio' | 'document' | 'archive' | 'code' | 'design')[] => {
  if (!mimeType) {
    // If no MIME type detected, show all categories
    return ['image', 'video', 'audio', 'document', 'archive', 'code', 'design'];
  }

  if (mimeType.startsWith('image/')) {
    return ['image', 'document']; // Images can be converted to other image formats or documents
  } else if (mimeType.startsWith('video/')) {
    return ['video', 'document']; // Videos can be converted to other video formats or documents
  } else if (mimeType.startsWith('audio/')) {
    return ['audio', 'document']; // Audio can be converted to other audio formats or documents
  } else if (mimeType.startsWith('text/') || mimeType.includes('document') || mimeType.includes('pdf')) {
    return ['document', 'code']; // Text/documents can be converted to other document formats or code
  } else if (mimeType.includes('json') || mimeType.includes('javascript') || mimeType.includes('xml')) {
    return ['code', 'document']; // Code files can be converted to other code formats or documents
  } else if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('archive')) {
    return ['archive', 'document']; // Archives can be converted to other archive formats or documents
  } else {
    // For unknown types, show all categories
    return ['image', 'video', 'audio', 'document', 'archive', 'code', 'design'];
  }
};

export default function DownloadComponent({
  data,
  filename = 'download',
  className,
  variant = 'outline',
  size = 'default',
  children,
  disabled = false,
  onDownload,
  allowedCategories,
}: DownloadComponentProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleDownload = (option: DownloadOption) => {
    try {
      const finalFilename = `${filename}.${option.extension}`;
      
      // Call custom download handler if provided
      if (onDownload) {
        onDownload(option.extension, finalFilename);
        setIsOpen(false);
        return;
      }

      // Default download behavior
      let blob: Blob;
      
      if (typeof data === 'string') {
        // Handle data URLs or text content
        if (data.startsWith('data:')) {
          const response = fetch(data);
          response.then(res => res.blob()).then(blobData => {
            downloadBlob(blobData, finalFilename, option.mimeType);
          });
          return;
        } else {
          // Text content
          blob = new Blob([data], { type: option.mimeType });
        }
      } else if (data instanceof Blob) {
        blob = data;
      } else if (data && typeof data === 'object' && 'name' in data && 'size' in data) {
        // File-like object
        blob = data as Blob;
      } else {
        throw new Error('Unsupported data type');
      }

      downloadBlob(blob, finalFilename, option.mimeType);
      setIsOpen(false);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const downloadBlob = (blob: Blob, filename: string, mimeType: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Get the MIME type and determine allowed categories
  const mimeType = detectFileType(data);
  const defaultAllowedCategories = getAllowedCategories(mimeType);
  const categoriesToShow = allowedCategories || defaultAllowedCategories;

  // Filter options based on allowed categories
  const filteredOptions = DOWNLOAD_OPTIONS.filter(option => 
    categoriesToShow.includes(option.category)
  );

  const groupedOptions = filteredOptions.reduce((acc, option) => {
    if (!acc[option.category]) {
      acc[option.category] = [];
    }
    acc[option.category].push(option);
    return acc;
  }, {} as Record<string, DownloadOption[]>);

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerTrigger asChild>
         <Button
           variant={variant}
           size={size}
           className={cn("btn-enhanced hover-lift hover:bg-blue-600 hover:text-white dark:hover:bg-blue-500 dark:hover:text-white hover:shadow-lg hover:scale-105 transition-all duration-200 ease-in-out", className)}
           disabled={disabled}
         >
          {children || (
            <>
              <DownloadIcon className="mr-2 h-4 w-4" />
              Download
            </>
          )}
        </Button>
      </DrawerTrigger>
      
       <DrawerContent className="max-h-[70vh] max-w-md mx-auto">
         <DrawerHeader className="pb-3">
           <DrawerTitle className="flex items-center gap-2 text-lg">
             <DownloadIcon className="h-4 w-4" />
             Choose Format
           </DrawerTitle>
           <DrawerDescription className="text-sm">
             Select download format
           </DrawerDescription>
         </DrawerHeader>
         
         <div className="px-4 pb-3 overflow-y-auto max-h-[50vh]">
           <div className="space-y-4">
             {Object.entries(groupedOptions).map(([category, options]) => (
               <div key={category} className="space-y-2">
                 <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                   {CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS]}
                 </h3>
                 <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-1.5">
                   {options.map((option) => {
                     // Use purple for design files, blue for others
                     const hoverColor = option.category === 'design' 
                       ? 'hover:bg-purple-600 hover:text-white dark:hover:bg-purple-500 dark:hover:text-white' 
                       : 'hover:bg-blue-600 hover:text-white dark:hover:bg-blue-500 dark:hover:text-white';
                     
                     return (
                     <Button
                       key={option.id}
                       variant="outline"
                       size="sm"
                       className={`h-auto p-2 flex flex-col items-center gap-1 ${hoverColor} hover:shadow-md hover:scale-105 hover:border-transparent transition-all duration-200 ease-in-out`}
                       onClick={() => handleDownload(option)}
                     >
                       <div className="flex items-center justify-center">
                         {option.icon}
                       </div>
                       <span className="text-xs font-medium leading-tight">{option.label}</span>
                     </Button>
                     );
                   })}
                 </div>
               </div>
             ))}
           </div>
         </div>
        
         <DrawerFooter className="pt-2">
           <Button
             variant="outline"
             size="sm"
             onClick={() => setIsOpen(false)}
             className="w-full hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-100 transition-colors duration-200"
           >
             Cancel
           </Button>
         </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
